import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Eye, Sparkles, Lightbulb, BookOpen, Link2, Calendar, User, Building2, TrendingUp } from 'lucide-react'
import { CASES_DATA } from '@/data/cases'
import { cn } from '@/lib/utils'

interface CaseDetailPageProps {
  caseId: string
  onBack: () => void
  onTemplateLink: (templateId: string) => void
}

const COVER_GRADIENTS: Record<string, string> = {
  'case-001': 'from-blue-500 to-indigo-600',
  'case-002': 'from-emerald-500 to-teal-600',
  'case-003': 'from-amber-500 to-orange-600',
  'case-004': 'from-purple-500 to-pink-600',
  'case-005': 'from-red-500 to-rose-600',
  'case-006': 'from-cyan-500 to-blue-600',
}

const COVER_ICONS: Record<string, string> = {
  'case-001': '📊',
  'case-002': '📈',
  'case-003': '️',
  'case-004': '💰',
  'case-005': '🔍',
  'case-006': '📋',
}

export function CaseDetailPage({ caseId, onBack, onTemplateLink }: CaseDetailPageProps) {
  const caseData = CASES_DATA.find(c => c.id === caseId)
  if (!caseData) {
    return (
      <div className="flex-1 overflow-auto bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 text-sm">未找到该案例</p>
          <Button variant="ghost" className="mt-2 text-gcb-primary" onClick={onBack}>返回列表</Button>
        </div>
      </div>
    )
  }

  const gradient = COVER_GRADIENTS[caseId] || 'from-blue-500 to-indigo-600'
  const icon = COVER_ICONS[caseId] || '📊'

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="max-w-4xl mx-auto p-6">
        {/* 返回按钮 */}
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gcb-primary mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          返回案例列表
        </button>

        {/* 封面横幅 */}
        <div className={cn('h-48 rounded-xl bg-gradient-to-br flex items-center justify-center mb-6 relative', gradient)}>
          <span className="text-7xl">{icon}</span>
          <div className="absolute bottom-3 right-3 bg-black/40 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" />
            {caseData.viewCount} 次浏览
          </div>
        </div>

        {/* 标题区 */}
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-900 mb-2">{caseData.name}</h1>
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{caseData.institution} · {caseData.department}</span>
            <span className="flex items-center gap-1"><User className="w-3 h-3" />{caseData.author}</span>
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{caseData.createdAt}</span>
          </div>
          <div className="flex gap-2 mt-3">
            <Badge variant="outline" className="text-xs">{caseData.businessLine}</Badge>
            {caseData.keywords.map(kw => (
              <Badge key={kw} variant="secondary" className="text-xs bg-gray-100 text-gray-600">{kw}</Badge>
            ))}
          </div>
        </div>

        {/* 简介 */}
        <SectionBlock icon={<BookOpen className="w-4 h-4" />} title="案例简介" gradient="from-blue-500 to-blue-600">
          <p className="text-sm text-gray-700 leading-relaxed">{caseData.intro}</p>
        </SectionBlock>

        {/* 背景模板 */}
        <SectionBlock icon={<Lightbulb className="w-4 h-4" />} title="背景模板" gradient="from-amber-500 to-amber-600">
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{caseData.background}</p>
        </SectionBlock>

        {/* 分析思路 */}
        <SectionBlock icon={<Sparkles className="w-4 h-4" />} title="分析思路" gradient="from-purple-500 to-purple-600">
          <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{caseData.analysisApproach}</div>
        </SectionBlock>

        {/* 业务成效 */}
        <SectionBlock icon={<TrendingUp className="w-4 h-4" />} title="业务成效" gradient="from-green-500 to-green-600">
          <div className="space-y-3">
            {/* 简要成效 */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Sparkles className="w-3.5 h-3.5 text-green-600" />
                <span className="text-xs font-semibold text-green-700">核心成效</span>
              </div>
              <p className="text-sm text-green-800 font-medium">{caseData.businessResults}</p>
            </div>
            {/* 详细成效 */}
            <div>
              <span className="text-xs font-medium text-gray-600">详细数据：</span>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line mt-1">{caseData.detailedResults}</p>
            </div>
          </div>
        </SectionBlock>

        {/* 模板链接 */}
        {caseData.templateLink && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-800">关联报表模板</p>
                  <p className="text-xs text-blue-600">{caseData.templateLink}</p>
                </div>
              </div>
              <Button
                size="sm"
                className="bg-gcb-primary hover:bg-gcb-primaryDark text-white text-xs h-8"
                onClick={() => caseData.templateId && onTemplateLink(caseData.templateId)}
              >
                查看模板 <Link2 className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/* ---- Section Block ---- */
function SectionBlock({
  icon,
  title,
  gradient,
  children,
}: {
  icon: React.ReactNode
  title: string
  gradient: string
  children: React.ReactNode
}) {
  return (
    <div className="bg-white rounded-lg border overflow-hidden mb-4">
      <div className={cn('px-4 py-2.5 bg-gradient-to-r text-white flex items-center gap-2', gradient)}>
        {icon}
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      <div className="p-4">{children}</div>
    </div>
  )
}
